import { MATERIAL_VIEWS, PredictionType, SEARCH_EVENT_TYPE, SEARCH_INPUT_TYPE } from '../../../constants/search';
import { ScrollQualityEventType } from '../../../hooks/quality-events/use-page-scroll-quality-event';
import { storageKeys } from '../../../utils/browser-storage/storage-keys';
import qualityEventsHelpers, { stringifyExtra } from '../quality-events.helpers';

export type QueryOriginType = 'typed' | 'paste' | 'suggestion' | 'next_search_token' | 'typo_suggestion';
export type TriggerActionType = 'enter' | 'search_button' | 'suggestion_click' | 'filter_change' | 'sort_change';
export type RequestReasonType = 'new_query' | 'refinement' | 'filter' | 'pagination';

export type ValueSuggestType = {
    suggest: string;
    query: string;
    searchRequestId: string | undefined;
    type: 'show' | 'click' | 'empty';
};

export type ApiParamsType = {
    test_segment?: number | string;
    auto_suggest?: number | string;
    intent?: number | string;
    spellCheck?: number;
    llmSuggest?: boolean | number | string;
};

export type RequestExtraType = {
    queryOrigin?: QueryOriginType;
    triggerAction?: TriggerActionType;
    requestReason?: RequestReasonType;
    apiParams?: ApiParamsType;
};

type AppearedInSearchParamsType = {
    query: string;
    total: number;
    searchRequestId: string | undefined;
    viewMode: MATERIAL_VIEWS;
    materials: Material[];
};

type ShowMoreMaterialsParamType = {
    portionNumber: number;
    searchRequestId?: string;
    limit: number;
    materials: Material[];
    offset: number;
};

const FIRST_MATERIAL_POSITION = 1;

export const SRCH_QUALITY_EVENTS = {
    valueSuggest({ query, suggest, searchRequestId, type }: ValueSuggestType) {
        qualityEventsHelpers.track({
            query,
            type: 'valueSuggest',
            extra: stringifyExtra({ suggest, type, search_request_id: searchRequestId })
        });
    },
    clearSearchField(onFinally?: () => void) {
        qualityEventsHelpers.track({ type: SEARCH_EVENT_TYPE.CLEAR_SEARCH_FIELD }).finally(onFinally);
    },
    emptyResultPage() {
        qualityEventsHelpers.track({
            type: 'emptyResultPage'
        });
    },
    viewSearchResults(viewMode: MATERIAL_VIEWS, searchRequestId: string | undefined) {
        qualityEventsHelpers.track({
            type: 'view_search_results',
            extra: stringifyExtra({ view_mode: `${viewMode}View`, search_request_id: searchRequestId })
        });
    },
    noResultsScreenShown(searchRequestId: string | undefined, suggest?: string) {
        qualityEventsHelpers.track({
            type: 'noResultsScreenShown',
            extra: stringifyExtra({ suggest, search_request_id: searchRequestId })
        });
    },
    appearedInSearch({ query, total, searchRequestId, viewMode, materials }: AppearedInSearchParamsType) {
        qualityEventsHelpers.track({
            type: 'appearedInSearch',
            query,
            total_results: total,
            extra: stringifyExtra({
                view_mode: `${viewMode}View`,
                materials: materials.slice(0, 36).map((m, index) => ({
                    material_id: m.id,
                    price: m.price,
                    quality_score: m.qualityScore,
                    position: index + FIRST_MATERIAL_POSITION
                })),
                search_request_id: searchRequestId
            })
        });
    },
    searchRequest({ query, extra }: { query?: string; extra?: RequestExtraType }) {
        const stored = window.storage.getItem(storageKeys.general.postponedQE);
        const existing = stored ? JSON.parse(stored) : {};

        window.storage.setItem(
            storageKeys.general.postponedQE,
            JSON.stringify({
                ...existing,
                type: 'Request',
                ...(query && { query }),
                source: window.location.href.indexOf('searchResults') >= 0 ? 'search' : 'catalog',
                extra: {
                    ...existing.extra,
                    ...(extra?.queryOrigin && { query_origin: extra.queryOrigin }),
                    ...(extra?.triggerAction && { trigger_action: extra.triggerAction }),
                    ...(extra?.requestReason && { request_reason: extra.requestReason }),
                    ...(extra?.apiParams && {
                        api_params: { ...existing.extra?.api_params, ...extra.apiParams }
                    })
                }
            })
        );
    },
    requestTrackPostponed(total: number, searchRequestId: string | undefined, apiParams?: ApiParamsType) {
        const extra = { api_params: apiParams, search_request_id: searchRequestId };
        const hasExtra = Object.values(extra).some((value) => value !== undefined);

        qualityEventsHelpers.trackPostponed({
            total_results: total,
            ...(apiParams?.test_segment !== undefined && { test_segment: String(apiParams.test_segment) }),
            ...(hasExtra && { extra })
        });
    },
    searchEvent(usedInputTypes: SEARCH_INPUT_TYPE[], source: string, query: string, type: string) {
        qualityEventsHelpers.track({
            extra: JSON.stringify(usedInputTypes),
            source,
            query,
            type
        });
    },
    predictionToken(query: string, prediction: PredictionType) {
        qualityEventsHelpers.track({
            type: 'selected_next_token',
            query,
            extra: JSON.stringify(prediction)
        });
    },
    showMoreMaterials({ portionNumber, limit, searchRequestId, materials, offset }: ShowMoreMaterialsParamType) {
        qualityEventsHelpers.track({
            type: 'showMoreMaterials',
            extra: stringifyExtra({
                portionNumber,
                limit,
                materials: materials.map((material, index) => ({
                    material_id: material.id,
                    price: material.price,
                    quality_score: material.qualityScore,
                    position: offset + index + FIRST_MATERIAL_POSITION
                })),
                search_request_id: searchRequestId
            })
        });
    },
    searchPageScrolling({
        pageUrl,
        searchRequestId,
        ...extra
    }: { pageUrl: string; viewMode?: MATERIAL_VIEWS; searchRequestId?: string } & ScrollQualityEventType) {
        qualityEventsHelpers.trackAndForget({
            type: 'pageScrolling',
            page_url: pageUrl,
            extra: JSON.stringify({ ...extra, search_request_id: searchRequestId })
        });
    },
    pageViewDuration(durationMs: number, page_url: string) {
        qualityEventsHelpers.trackAndForget({
            type: 'pageViewDuration',
            page_url,
            extra: JSON.stringify({
                durationS: Math.floor(durationMs / 1000),
                timestamp: Date.now()
            })
        });
    }
};
